import { uri } from "constants/common";
import { FileText, Languages, Upload, X } from "lucide-react";
import React, { useRef, useState } from "react";
import Api from "services/api";

const LANGUAGES = [
  "Arabic", "Bengali", "Chinese (Simplified)", "Chinese (Traditional)",
  "Dutch", "French", "German", "Greek", "Gujarati", "Hebrew", "Hindi",
  "Indonesian", "Italian", "Japanese", "Kannada", "Korean", "Malay",
  "Marathi", "Nepali", "Persian", "Polish", "Portuguese", "Punjabi",
  "Romanian", "Russian", "Somali", "Spanish", "Swahili", "Tagalog",
  "Tamil", "Telugu", "Thai", "Turkish", "Ukrainian", "Urdu", "Vietnamese",
];

export default function AiTranslationModal({ open, onClose }) {
  const fileInputRef = useRef(null);

  const [file, setFile]               = useState(null);
  const [language, setLanguage]       = useState("");
  const [status, setStatus]           = useState("idle"); // idle | loading | done | error
  const [errorMsg, setErrorMsg]       = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadName, setDownloadName] = useState("");
  const [dragOver, setDragOver]       = useState(false);

  if (!open) return null;

  const reset = () => {
    setFile(null);
    setLanguage("");
    setStatus("idle");
    setErrorMsg("");
    setDownloadUrl(null);
    setDownloadName("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const pickFile = (picked) => {
    if (!picked) return;
    const allowed = ["pdf", "txt"];
    const ext = picked.name.split(".").pop().toLowerCase();
    if (!allowed.includes(ext)) {
      setErrorMsg("Unsupported file type. Please upload PDF, DOCX, DOC, or TXT.");
      return;
    }
    setErrorMsg("");
    setFile(picked);
    setStatus("idle");
    setDownloadUrl(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files[0]);
  };

  const handleTranslate = async () => {
    if (!file || !language) return;

    setStatus("loading");
    setErrorMsg("");
    setDownloadUrl(null);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("target_language", language);

    try {
      const res = await fetch(`${uri}/ai/translate`, {
        method: "POST",
        headers: { Authorization: `Bearer ${Api._token}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Translation failed.");
      }

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const ext  = file.name.split(".").pop().toLowerCase();
      const orig = file.name.replace(/\.[^.]+$/, "");
      setDownloadUrl(url);
      setDownloadName(`${orig}_translated_${language}.${ext}`);
      setStatus("done");
    } catch (e) {
      setErrorMsg(e.message || "Something went wrong.");
      setStatus("error");
    }
  };

  const handleDownload = () => {
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = downloadName;
    a.click();
  };

  return (
    <div className="ai_modal_backdrop" onClick={handleClose}>
      <div className="ai_modal_box" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="ai_modal_header">
          <div className="ai_modal_title">
            <Languages size={20} strokeWidth={2} />
            AI Translation
          </div>
          <button className="ai_modal_close" onClick={handleClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="ai_modal_body">

          {/* Drop zone */}
          <div
            className={`ai_dropzone ${dragOver ? "drag_over" : ""} ${file ? "has_file" : ""}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.txt"
              style={{ display: "none" }}
              onChange={(e) => pickFile(e.target.files[0])}
            />
            {file ? (
              <>
                <FileText size={32} strokeWidth={1.5} className="ai_drop_icon file_ready" />
                <p className="ai_drop_name">{file.name}</p>
                <p className="ai_drop_hint">Click to replace</p>
              </>
            ) : (
              <>
                <Upload size={32} strokeWidth={1.5} className="ai_drop_icon" />
                <p className="ai_drop_label">Drop your document here</p>
                <p className="ai_drop_hint">or click to browse — PDF or TXT</p>
              </>
            )}
          </div>

          {/* Language selector */}
          <div className="ai_field_group">
            <label className="ai_field_label">Target Language</label>
            <select
              className="ai_select"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="">Select a language…</option>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          {/* Error */}
          {errorMsg && <p className="ai_error">{errorMsg}</p>}

          {/* Loader */}
          {status === "loading" && (
            <div className="ai_loader_wrap">
              <div className="ai_spinner"></div>
              <p className="ai_loader_text">Translating your document…</p>
            </div>
          )}

          {/* Done — download */}
          {status === "done" && (
            <div className="ai_done_wrap">
              <div className="ai_done_check">✓</div>
              <p className="ai_done_text">Translation complete!</p>
              <button className="ai_download_btn" onClick={handleDownload}>
                Download Translated Document
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {(status === "idle" || status === "error") && (
          <div className="ai_modal_footer">
            <button className="ai_cancel_btn" onClick={handleClose}>Cancel</button>
            <button
              className="ai_translate_btn"
              onClick={handleTranslate}
              disabled={!file || !language}
            >
              Translate
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
